import { EuroOutlined, NumbersOutlined, PercentOutlined } from '@mui/icons-material'
import { Box, FormControl, InputAdornment, Stack } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import GridToolbar from '@/components/dataDisplay/GridToolbar'
import CustomButton from '@/components/inputs/CustomButton'
import CustomSelectField from '@/components/inputs/CustomSelectField'
import FloatTextField from '@/components/inputs/FloatTextField'
import IntegerTextField from '@/components/inputs/IntegerTextField'
import { DEFAULT_CURRENCY, MAXIMUM_MW_PER_PTU } from '@/features/bidding/constants'
import { BulkFillMode } from '@/features/bidding/types/bid'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'

/**
 * Module augmentation is necessary to use slotProps without TypeScript errors.
 */
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends PriceDataGridToolbarProps {}
}

type PriceDataGridToolbarProps = {
  onFill: (value: number | undefined, priceColumn: number | undefined, bulkFillMode: BulkFillMode) => void
  onSetPriceColumnsClick: () => void
  acceptsOnlyPositiveInteger: boolean
  deliveryDay: MarketDate
  priceColumns: number[]
  unit: string
}

function PriceDataGridToolbar({
  onFill,
  onSetPriceColumnsClick,
  acceptsOnlyPositiveInteger = false,
  deliveryDay,
  priceColumns,
  unit,
}: Readonly<PriceDataGridToolbarProps>) {
  const { t, i18n } = useTranslation()

  const [bulkFillSelectedColumn, setBulkFillSelectedColumn] = useState<number | undefined>()
  const [bulkFillMode, setBulkFillMode] = useState(BulkFillMode.ABSOLUTE_VALUE)
  const [bulkFillValue, setBulkFillValue] = useState<number | undefined>()

  const actions = (
    <>
      <Stack sx={{ flexDirection: 'row', gap: 1 }}>
        <BulkFillTypeSelect bulkFillMode={bulkFillMode} setBulkFillMode={setBulkFillMode} />
        <FormControl>
          <BulkFillInputField
            acceptsOnlyPositiveInteger={acceptsOnlyPositiveInteger && bulkFillMode == BulkFillMode.ABSOLUTE_VALUE}
            bulkFillMode={bulkFillMode}
            setBulkFillValue={setBulkFillValue}
            unit={unit}
          />
        </FormControl>
        <BulkFillColumnsSelect
          bulkFillSelectedColumn={bulkFillSelectedColumn}
          priceColumns={priceColumns}
          setBulkFillSelectedColumn={setBulkFillSelectedColumn}
        />
      </Stack>
      <CustomButton
        data-testid="fill-column-button"
        disabled={bulkFillValue == undefined || bulkFillSelectedColumn == undefined}
        variant="contained"
        onClick={() => onFill(bulkFillValue, bulkFillSelectedColumn, bulkFillMode)}
      >
        {t('bidding.create_bid.fill')}
      </CustomButton>
      <CustomButton
        data-testid="price-column-button"
        startIcon={<EuroOutlined />}
        variant="outlined"
        onClick={onSetPriceColumnsClick}
      >
        {t('bidding.create_bid.set_price_columns')}
      </CustomButton>
    </>
  )

  return (
    <GridToolbar
      actions={actions}
      filters={
        <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
          <DatePicker
            disabled
            defaultValue={deliveryDay.getStartOfDay()}
            enableAccessibleFieldDOMStructure={false}
            format="DD"
            label={t('bidding.create_bid.bid_date')}
            sx={{ width: 180 }}
          />
        </LocalizationProvider>
      }
    />
  )
}

const BulkFillTypeSelect = ({ bulkFillMode, setBulkFillMode }) => {
  const { t } = useTranslation()

  return (
    <CustomSelectField
      data-testid="bulk-fill-type"
      id="bulk-fill"
      label={t('bidding.create_bid.bulk_fill')}
      options={[
        {
          id: BulkFillMode.OFFER_RATIO,
          value: BulkFillMode.OFFER_RATIO,
          label: t('bidding.create_bid.offer_ratio'),
          icon: <PercentOutlined />,
        },
        {
          id: BulkFillMode.ABSOLUTE_VALUE,
          value: BulkFillMode.ABSOLUTE_VALUE,
          label: t('bidding.create_bid.absolute_value'),
          icon: <NumbersOutlined />,
        },
      ]}
      value={bulkFillMode}
      onChange={(event) => setBulkFillMode(event.target.value)}
    />
  )
}

const BulkFillInputField = ({ acceptsOnlyPositiveInteger, bulkFillMode, setBulkFillValue, unit }) => {
  if (acceptsOnlyPositiveInteger) {
    return (
      <Box width={120}>
        <IntegerTextField
          max={MAXIMUM_MW_PER_PTU}
          min={0}
          textFieldProps={{
            inputProps: { 'data-testid': 'bulk-fill-value' },
            label: 'Value',
          }}
          onValueChange={setBulkFillValue}
        />
      </Box>
    )
  }
  return (
    <Box width={120}>
      <FloatTextField
        max={MAXIMUM_MW_PER_PTU}
        min={0}
        textFieldProps={{
          InputProps: {
            endAdornment: (
              <InputAdornment position="end">{bulkFillMode == BulkFillMode.OFFER_RATIO ? '%' : unit}</InputAdornment>
            ),
          },
          inputProps: { 'data-testid': 'bulk-fill-value' },
          label: 'Value',
        }}
        onValueChange={setBulkFillValue}
      />
    </Box>
  )
}

function BulkFillColumnsSelect({ bulkFillSelectedColumn, setBulkFillSelectedColumn, priceColumns }) {
  const { t, i18n } = useTranslation()

  useEffect(() => {
    if (priceColumns.length === 1) {
      setBulkFillSelectedColumn(priceColumns[0])
    }
  }, [priceColumns, setBulkFillSelectedColumn])

  const options = priceColumns.map((price: number) => {
    const formattedPrice = new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: DEFAULT_CURRENCY,
    }).format(price)

    return {
      id: price,
      value: price,
      label: formattedPrice,
      'data-testid': `bulk-fill-column-${price}`,
    }
  })
  return (
    <CustomSelectField
      data-testid="bulk-fill-columns"
      id="bulk-fill-columns"
      label={t('bidding.create_bid.column')}
      options={options}
      sx={{ minWidth: 100 }}
      value={bulkFillSelectedColumn ?? ''}
      onChange={(event) => setBulkFillSelectedColumn(event.target.value)}
    />
  )
}

export default PriceDataGridToolbar
